import { createAsyncThunk, createSlice, } from "@reduxjs/toolkit";
import { getPlayer } from "../../api/api";
import { evolutionPathParam, playerEvolutionPath, } from "../../evolutions/evolutionPath";
export const getRolesPlusOrPlusPlus = ({ playerRoleId, rolesPlus, }) => {
    if (rolesPlus?.includes(playerRoleId + 100))
        return "plusPlus";
    if (rolesPlus?.includes(playerRoleId))
        return "plus";
    return null;
};
// Moved here from playersSlice.ts to avoid circular dependency
export const bodyTypeIdToName = {
    1: ["Avg & Lean"],
    2: ["Avg & Normal"],
    3: ["Avg & Stocky"],
    4: ["Tall & Lean"],
    5: ["Tall & Normal"],
    6: ["Tall & Stocky"],
    7: ["Short & Lean"],
    8: ["Short & Normal"],
    9: ["Short & Stocky"],
    11: ["Very Tall & Lean"],
    12: ["Unique", "Lean"],
    13: ["Unique", "Normal"],
    14: ["Unique", "Stocky"],
    15: ["-", "-"],
};
// Initial state
const initialState = {
    playerStatus: "idle",
    player: null,
    requestKey: null,
};
const typeOf = ({ type, evolutionPath }) => type ?? (evolutionPath?.length ? "evo-finder" : "base");
// The path goes through the URL codec, so a missing pathway and an all-linear
// one key the same request.
const requestKeyOf = (args) => `${args.playerId}|${args.evoPathId ?? ""}|${args.evolutionPath?.length
    ? evolutionPathParam(args.evolutionPath, args.pathwayPath)
    : ""}|${typeOf(args)}`;
export const fetchPlayer = createAsyncThunk("player/fetchPlayer", async (args) => {
    const response = await getPlayer({
        resourceId: args.playerId,
        evoPathId: args.evoPathId,
        evolutionPath: args.evolutionPath,
        pathwayPath: args.pathwayPath,
        type: typeOf(args),
    });
    return response;
}, {
    // The list prefetches on press and the detail fetches on mount; the second
    // of those is a no-op while the first is in flight or already landed.
    // Relies on the detail screen dispatching clearPlayer on unmount: without
    // that reset, reopening the same player would skip the fetch and stay on
    // the seeded list row.
    condition: (args, { getState }) => {
        const { playerStatus, requestKey } = getState()
            .playerReducer;
        return !(requestKey === requestKeyOf(args) &&
            (playerStatus === "pending" || playerStatus === "fulfilled"));
    },
});
/**
 * What a list does on press: seed the detail with the row it already has, so
 * the screen paints at once, and start the full fetch in parallel with the
 * navigation rather than after the detail has mounted.
 */
export const openPlayer = (player) => (dispatch) => {
    dispatch(setInitialPlayer(player));
    dispatch(fetchPlayer({
        playerId: player.resourceId,
        evoPathId: player.evoPathId,
        ...playerEvolutionPath(player),
    }));
};
// Create the slice
const playerSlice = createSlice({
    name: "player",
    initialState,
    extraReducers: (builder) => {
        builder.addCase(fetchPlayer.fulfilled, (state, action) => {
            // A superseded request (another player, or another path) landing late.
            if (requestKeyOf(action.meta.arg) !== state.requestKey)
                return;
            state.player = { ...action.payload, detailedPlayer: true };
            state.playerStatus = "fulfilled";
        });
        builder.addCase(fetchPlayer.pending, (state, action) => {
            state.playerStatus = "pending";
            state.requestKey = requestKeyOf(action.meta.arg);
        });
        builder.addCase(fetchPlayer.rejected, (state, action) => {
            if (requestKeyOf(action.meta.arg) !== state.requestKey)
                return;
            state.playerStatus = "rejected";
        });
    },
    reducers: {
        setInitialPlayer: (state, action) => {
            // SearchedPlayer is a structurally-different partial seed used to avoid
            // a flash of the previous player; fetchPlayer replaces it once resolved.
            state.player = action.payload;
        },
        clearPlayer: (state) => {
            state.player = null;
            state.playerStatus = "idle";
            state.requestKey = null;
        },
    },
});
export const { setInitialPlayer, clearPlayer } = playerSlice.actions;
export default playerSlice.reducer;
// export selector:
export const getPlayerSelector = (state) => state.playerReducer;
//# sourceMappingURL=playerSlice.js.map